import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { SyllabusQuery, SyllabusService } from '@examdojo/category/v2';
import { ReviewPermissionsQuery } from '@examdojo/review';
import { mapToVoid } from '@examdojo/rxjs';
import { first, tap } from 'rxjs';
import { RootUrlParts, SyllabusUrlParts } from '../app.model';
import { ActiveSyllabusManager } from '../storage/active-syllabus-manager';
import { SYLLABUS_SLUG_PARAM } from './validation-routing.model';

export const validationSyllabusesResolver: ResolveFn<void> = (route) => {
  const syllabusService = inject(SyllabusService);
  const syllabusQuery = inject(SyllabusQuery);
  const reviewPermissionsQuery = inject(ReviewPermissionsQuery);
  const activeSyllabusManager = inject(ActiveSyllabusManager);
  const router = inject(Router);

  const syllabusSlugParam = route.paramMap.get(SYLLABUS_SLUG_PARAM);
  if (!syllabusSlugParam) {
    // This shouldn't happen since guard handles redirect, but safety fallback
    router.navigate(['/syllabus']);
    return syllabusService.fetchSyllabuses().pipe(first(), mapToVoid());
  }

  return syllabusService.fetchSyllabuses().pipe(
    first(),
    tap((syllabuses) => {
      if (!syllabuses.length) {
        return;
      }

      const targetSyllabus = syllabusQuery.getBySlug(syllabusSlugParam);
      if (!targetSyllabus) {
        // Syllabus doesn't exist, redirect back to syllabus selection
        router.navigate([`/${RootUrlParts.Syllabus}`]);
        return;
      }

      const syllabusesWithPermissions = reviewPermissionsQuery.getAllowedSyllabuses();
      const hasPermissions = syllabusesWithPermissions.includes(targetSyllabus.id);

      if (!hasPermissions && syllabusesWithPermissions.length > 0) {
        // No permissions for this syllabus, redirect to one with permissions
        const firstAvailableSyllabus = syllabuses.find((s) => s.id === syllabusesWithPermissions[0]);
        if (firstAvailableSyllabus) {
          router.navigate([`/${RootUrlParts.Syllabus}/${firstAvailableSyllabus.slug}/${SyllabusUrlParts.Validate}`]);
        }
        return;
      }

      // Valid syllabusSlug - set active and save slug via ActiveSyllabusManager
      syllabusService.setActive(targetSyllabus.id);
      activeSyllabusManager.setActiveSyllabusSlug(targetSyllabus.slug);
    }),
    mapToVoid(),
  );
};
